import { MeshRenderer } from '@ikea-aoa/elements-3d';
import { ModelData, ProductData, SceneObject } from '@ikea-aoa/component-view-3d';

import {
	Lightbox,
	PopOver,
	PopOverSceneItemData,
	RangeSettings,
	SceneItemData,
	SofaConfiguration,
	Settings,
	ShoppingItem,
	IModuleSwiperItem,
} from 'sofa-reborn-react';
import { SceneLogic } from './SceneLogic';
import { UiController } from './UiController';
import { ProductConverter } from '../../util/ProductConverter';
import { ProductService, KompisDefaultFields } from '@inter-ikea-kompis/services';

type Props = {
	moduleItems: IModuleSwiperItem[];
	unfinishedSection?: PopOver;
	currentLightbox: Lightbox;
	rangeSettings: RangeSettings;
	sofaModels: ModelData;
	products: ProductData;
	noValidConnectionPopoverShowing?: boolean;
	hasVisitScene: boolean;
	coverOnly: boolean;
	settings: Settings;

	setUnfinishedModule: (value: PopOverSceneItemData) => void;
	showPopOver: (value: PopOver) => void;
	hidePopOver: (value: PopOver) => void;
	saveSceneData: (
		sceneItems: SceneItemData[],
		sofaConfiguration: SofaConfiguration,
		articlesPlaceable: { [key: string]: boolean },
		backrestsMissingCushion: boolean,
		rangeSettings: RangeSettings,
		sofaModels: ModelData,
		products: ProductData,
		isOverDropArea: boolean
	) => void;
	setShoppingItems: (shoppingItems: ShoppingItem[]) => void;
	setFaultySections: (
		unfinishedSections: PopOverSceneItemData[],
		missingCoverSections: PopOverSceneItemData[],
		standaloneSofabed: PopOverSceneItemData[]
	) => void;
	setPersistedConfiguration: (value: string) => void;
	clearAdditionalCoverItems: () => void;
};

export class SceneDataUtils {
	constructor(
		private logic: SceneLogic,
		private uiController: UiController,
		private props: Props
	) {}

	public updateProps(props: Props): void {
		this.props = props;
	}

	public getSceneItems = (): SceneItemData[] => {
		const sceneItems: SceneItemData[] = [];
		this.logic.sceneItems.forEach((value) => {
			const { articleId, id } = value;
			sceneItems.push({ articleId, id });
		});

		return sceneItems;
	};

	public async updateSceneData(isOverDropArea = false): Promise<void> {
		const swiperArticles = this.props.moduleItems.map((i) => i.id);
		if (!this.logic) {
			return;
		}
		const articlesPlaceable = this.logic.getArticlesPlaceableStatuses(swiperArticles);
		const configuration = this.logic.getSofaConfiguration(this.props.coverOnly);
		const unfinishedSections = !this.logic.isModularSofa()
			? this.logic.getUnfinishedSections()
			: [];
		const standaloneSofabed = this.logic.getStandaloneSofabed();
		const backrestsMissingCushion =
			this.props.rangeSettings.rangeId === 'vallentuna' &&
			this.logic.objectManager.cushionManager.getBackrestsMissingCushion().length > 0;

		this.props.saveSceneData(
			this.getSceneItems(),
			configuration,
			articlesPlaceable,
			backrestsMissingCushion,
			this.props.rangeSettings,
			this.props.sofaModels,
			this.props.products,
			isOverDropArea
		);
		const shoppingItems = await this.getShoppingItems(
			this.getSceneItems(),
			this.props.products,
			this.props.rangeSettings,
			this.props.coverOnly,
			this.props.settings
		);
		this.props.setShoppingItems(shoppingItems);
		const missingCoverPopOverData = this.logic
			.getMissingCoverSections()
			.map((i) => this.uiController.getPopOverSceneItemData(i) as PopOverSceneItemData);

		const unfinishedPopOverData = unfinishedSections.map(
			(i) => this.uiController.getPopOverSceneItemData(i) as PopOverSceneItemData
		);
		const standaloneSofabedPopOverData = standaloneSofabed.map(
			(i) => this.uiController.getPopOverSceneItemData(i) as PopOverSceneItemData
		);
		this.props.setFaultySections(
			unfinishedPopOverData,
			missingCoverPopOverData,
			standaloneSofabedPopOverData
		);

		/* Save configuration to session storage */
		if (this.logic.objectManager.sceneItems.size > 0) {
			this.props.setPersistedConfiguration(JSON.stringify(configuration));
		} else {
			if (this.props.hasVisitScene) {
				this.props.setPersistedConfiguration(JSON.stringify('EMPTY'));
			}
		}
	}

	public updateNoValidPopover(): void {
		/* Check if any of the modules in the selected color can be attached to the unfinished module.
               If no module can be attached we can't make the module "finished" and we should show the popup */
		const showUnfinishedPopOver = this.props.unfinishedSection
			? !this.props.unfinishedSection.showPopOver
			: true;
		const unfinishedSections = !this.logic.isModularSofa()
			? this.logic.getUnfinishedSections()
			: [];
		if (
			this.props.currentLightbox !== 'cover-picker' &&
			showUnfinishedPopOver &&
			unfinishedSections.length > 0
		) {
			let noPlacementPointForUnfinishedModule = false;
			const unfinishedSceneObject = unfinishedSections[0];
			const unfinishedArticlesPlaceable = this.logic.getUnfinishedSectionPlaceableStatus(
				unfinishedSceneObject as SceneObject
			);
			noPlacementPointForUnfinishedModule = Object.values(unfinishedArticlesPlaceable).every(
				(value) => value === false
			);

			const isCorner = Object.values(this.props.products).some((product) => {
				return (
					(product.productId === unfinishedSceneObject.articleId ||
						product.previousId === unfinishedSceneObject.articleId) &&
					product.filter.family === 'CORNER'
				);
			});
			/* Only show unfinished section popup if the unfinished section is a corner */
			if (noPlacementPointForUnfinishedModule && isCorner) {
				if (this.props.rangeSettings.rangeName === 'KIVIK') {
					return;
				}
				/* Show popup with keep or remove unfinished module */
				if (unfinishedSceneObject && unfinishedSceneObject.node) {
					const component = unfinishedSceneObject.node.getComponent('MeshRenderer') as MeshRenderer;
					const screenPosition = component.getScreenPosition(true);
					const popoverData = this.uiController.getPopOverSceneItemData(unfinishedSceneObject);

					this.props.setUnfinishedModule(popoverData as PopOverSceneItemData);
					if (this.props.noValidConnectionPopoverShowing) {
						this.props.hidePopOver({ type: 'no-valid-connection' });
					}
					// setTimeout to solve issue with kompis popover-balloon position
					setTimeout(() => {
						this.props.showPopOver({
							type: 'no-valid-connection',
							top: screenPosition.y,
							left: screenPosition.x,
						});
					}, 100);
				}
			}
		}
	}

	public async getShoppingItems(
		sceneItems: SceneItemData[],
		products: ProductData,
		rangeSettings: RangeSettings,
		coverOnly: boolean,
		settings: Settings
	): Promise<ShoppingItem[]> {
		let shoppingItems = ProductConverter.getShoppingItemsFromScene(
			sceneItems,
			products,
			rangeSettings,
			coverOnly
		);

		if (coverOnly) {
			const productService = new ProductService({
				locale: settings.locale,
				localizationPrefixApplication: settings.localizationPrefixApplication,
				localizationEndpoints: settings.localizationEndpoints,
			});

			const articleProducts = await productService.getShoppingProducts(
				shoppingItems,
				KompisDefaultFields
			);

			const filteredCoverProducts = articleProducts.filter(
				(prod) => prod.product.valid && prod.product.content?.mainTypeCode === '00062'
			);

			const coverIds = filteredCoverProducts.map(
				(prod) => prod.product.content?.itemNoLocal || prod.product.content?.itemNoGlobal
			);

			shoppingItems = shoppingItems.filter((item) => coverIds.includes(item.id));
		}
		return shoppingItems;
	}
}
